import { z } from 'zod'
import { paginatedSearchQuery } from '../zod/pagination'
import { user } from '../user/user'
import { interviewCampaign, salesCampaign } from '../campaign/campaign'

const campaignActivityAttributes = z.union([
    interviewCampaign.omit({ id: true, createdAt: true, updatedAt: true }).partial(),
    salesCampaign.omit({ id: true, createdAt: true, updatedAt: true }).partial(),
])

const activityUnion = z.discriminatedUnion('event', [
    z.object({
        event: z.literal('App\\Domains\\User\\Events\\Updated'),
        attributes: user.omit({ id: true, createdAt: true, updatedAt: true, avatarConfig: true }).partial(),
    }),
    z.object({
        event: z.literal('App\\Domains\\User\\Events\\Impersonated'),
        attributes: z.null(),
    }),
    z.object({
        event: z.literal('App\\Domains\\User\\Events\\Blocked'),
        attributes: z.null(),
    }),
    z.object({
        event: z.literal('App\\Domains\\User\\Events\\Unblocked'),
        attributes: z.null(),
    }),
    z.object({
        event: z.literal('App\\Domains\\User\\Events\\Authenticated'),
        attributes: z.null(),
    }),
    z.object({
        event: z.literal('App\\Domains\\User\\Events\\PasswordReset'),
        attributes: z.null(),
    }),
    z.object({
        event: z.literal('App\\Domains\\User\\Events\\Registered'),
        attributes: user.omit({ id: true, createdAt: true, updatedAt: true }).partial(),
    }),
    z.object({
        event: z.literal('App\\Domains\\User\\Events\\Verified'),
        attributes: user.omit({ id: true, createdAt: true, updatedAt: true }).partial(),
    }),
    z.object({
        event: z.literal('App\\Domains\\Campaign\\Events\\Updated'),
        attributes: campaignActivityAttributes,
    }),
    z.object({
        event: z.literal('App\\Domains\\Campaign\\Events\\Created'),
        attributes: campaignActivityAttributes,
    }),
    z.object({
        event: z.literal('App\\Domains\\User\\Events\\PasswordForgotten'),
        attributes: z.null(),
    }),
    // Invitation Events
    z.object({
        event: z.literal('App\\Domains\\Invitation\\Events\\Accepted'),
        attributes: z.null(),
    }),
    z.object({
        event: z.literal('App\\Domains\\Invitation\\Events\\Declined'),
        attributes: z.null(),
    }),
])

export const activity = z
    .object({
        id: z.string(),
        // Empty when the WordPress plugin or Evaa itself made the change; source says which.
        userId: z.string().nullable(),
        source: z.enum(['user', 'wordpress_plugin', 'automatic']),
        subjectId: z.string(),
        subjectType: z.string(),
        createdAt: z.coerce.date(),
        updatedAt: z.coerce.date(),
    })
    .and(activityUnion)

export type Activity = z.infer<typeof activity>

export const activityQuery = paginatedSearchQuery({
    filters: z.object({
        userId: z.string().array().optional(),
        subjectId: z.string().array().optional(),
        subjectType: z.string().array().optional(),
    }),
    sort: z.enum(['created_at', '-created_at']),
})

export type ActivityQuery = z.infer<typeof activityQuery>

/**
 * t('App\\Domains\\User\\Events\\Authenticated')
 * t('App\\Domains\\User\\Events\\Updated')
 * t('App\\Domains\\User\\Events\\Impersonated')
 * t('App\\Domains\\User\\Events\\Blocked')
 * t('App\\Domains\\User\\Events\\Unblocked')
 * t('App\\Domains\\User\\Events\\PasswordReset')
 * t('App\\Domains\\User\\Events\\Registered')
 * t('App\\Domains\\User\\Events\\Verified')
 * t('App\\Domains\\Campaign\\Events\\Updated')
 * t('App\\Domains\\Campaign\\Events\\Created')
 * t('App\\Domains\\User\\Events\\PasswordForgotten')
 * t('App\\Domains\\Invitation\\Events\\Accepted')
 * t('App\\Domains\\Invitation\\Events\\Declined')
 * t('monthly')
 * t('yearly')
 * t('companyUserRole.member')
 * t('companyUserRole.owner')
 */
